import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from '@/utils/api/axios';
import { IOrganizationItem } from '@/types/organization';
import { fillWithZeros } from '@/utils/formatters/format-number';
import { CustomScreen } from '@/components/layout/custom-screen';
import BillingOrderNewEditStepsForm from '@/sections/administrative/accounts/organizations/forms/billing-order-new-edit-steps-form';

const fetchOrderData = async (orderId: string) => {
  const response = await axios.get(`orders/${orderId}`);
  return response.data;
};

const fetchOrganizationData = async (organizationId: string) => {
  const response = await axios.get(`organizations/${organizationId}`);
  return response.data;
};

export default function Page() {
  const { t } = useTranslation();
  const { id: orderId } = useParams();

  const { data: orderData, isLoading: orderIsLoading } = useQuery({
    queryKey: ['billing-order', orderId, 'edit'],
    queryFn: () => fetchOrderData(orderId!),
    enabled: !!orderId,
    refetchOnMount: 'always',
  });

  const organizationId = orderData?.data?.organization?._id || orderData?.data?.organization;

  const { data: organizationData, isLoading: organizationIsLoading } = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => fetchOrganizationData(organizationId),
    enabled: !!organizationId,
  });

  const organization: IOrganizationItem = organizationData?.data;

  return (
    <>
      <Helmet>
        <title>
          {t('Edit Billing Order')}{' '}
          {orderData?.data?.orderNumber ? `#${fillWithZeros(orderData.data.orderNumber, 6)}` : ''}
        </title>
      </Helmet>

      <CustomScreen
        heading={
          orderData?.data?.orderNumber
            ? `${t('Edit Billing Order')} #${fillWithZeros(orderData.data.orderNumber, 6)}`
            : t('Edit Billing Order')
        }
        links={[
          { name: t('Accounts'), href: '/management/accounts' },
          { name: t('Billing Orders'), href: '/management/accounts/billing-orders' },
          {
            name: orderData?.data?.orderNumber
              ? `#${fillWithZeros(orderData.data.orderNumber, 6)}`
              : t('Edit'),
          },
        ]}
        isLoading={orderIsLoading || organizationIsLoading}
      >
        {orderData?.data && organization ? (
          <BillingOrderNewEditStepsForm
            currentExistsOrder={orderData.data}
            currentOrganization={organization}
          />
        ) : null}
      </CustomScreen>
    </>
  );
}
